import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, RefreshCw, Terminal } from "lucide-react";
import api from "@/lib/api";
import { PageTitle } from "@/components/CrudPage";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function CodeBlock({ text, filename }) {
  const copy = () => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); };
  const download = () => {
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = filename; a.click();
    toast.success(`Downloaded ${filename}`);
  };
  return (
    <div className="rounded-xl border border-border bg-[#0c0c0e] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-secondary/40">
        <span className="font-mono text-xs text-muted-foreground">{filename}</span>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={copy} data-testid="config-copy"><Copy size={14} className="mr-1" />Copy</Button>
          <Button size="sm" variant="ghost" onClick={download} data-testid="config-download"><Download size={14} className="mr-1" />Download</Button>
        </div>
      </div>
      <pre className="p-4 overflow-x-auto text-xs font-mono leading-relaxed max-h-[520px] text-[#d6d3cb]">{text}</pre>
    </div>
  );
}

const DEPLOY = `# ── InContact Cloud PBX · Asterisk config install (Azure VM) ──
# Run these on the VM after downloading all four config files.

# 1) Drop every generated file in place
sudo cp pjsip.conf       /etc/asterisk/pjsip.conf
sudo cp extensions.conf  /etc/asterisk/extensions.conf
sudo cp queues.conf      /etc/asterisk/queues.conf
sudo cp voicemail.conf   /etc/asterisk/voicemail.conf
sudo chown asterisk:asterisk /etc/asterisk/*.conf

# 2) Reload Asterisk so all changes take effect
sudo asterisk -rx "core reload"
sudo asterisk -rx "dialplan reload"
sudo asterisk -rx "pjsip reload"
sudo asterisk -rx "queue reload all"
sudo asterisk -rx "voicemail reload"

# 3) Verify everything loaded
sudo asterisk -rx "pjsip show endpoints"
sudo asterisk -rx "dialplan show from-internal"
sudo asterisk -rx "queue show"

# ── Feature codes now live in the dialplan ──
#   *97        check your own voicemail
#   *98        voicemail login (any mailbox)
#   *411       company phonebook (dial-by-name directory)
#   *55<ext>   leave a voicemail directly for an extension
#
# Ring groups & queues are reachable by dialling their group/queue extension.
# IVR greetings: record each menu prompt as /var/lib/asterisk/sounds/custom/ivr-<id>.gsm
# (the dialplan already references it — the file name is shown in extensions.conf).
#
# Regenerate & reload this config after every change you make in the console.`;

export default function ConfigExport() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const gen = () => { setLoading(true); api.get("/config/asterisk").then((r) => { setCfg(r.data); setLoading(false); }); };
  useEffect(() => { gen(); }, []);

  return (
    <div>
      <PageTitle
        title="Config Export"
        subtitle="Turn everything you built here into ready-to-deploy Asterisk (PJSIP) configuration for your Azure VM."
        actions={<Button onClick={gen} disabled={loading} data-testid="config-regen"
          className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-hover))] font-semibold">
          <RefreshCw size={15} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />Regenerate</Button>}
      />

      {loading && <div className="text-muted-foreground">Generating…</div>}
      {cfg && (
        <Tabs defaultValue="pjsip">
          <TabsList className="bg-secondary/40 border border-border">
            <TabsTrigger value="pjsip" data-testid="tab-pjsip">pjsip.conf</TabsTrigger>
            <TabsTrigger value="dialplan" data-testid="tab-dialplan">extensions.conf</TabsTrigger>
            <TabsTrigger value="queues" data-testid="tab-queues">queues.conf</TabsTrigger>
            <TabsTrigger value="voicemail" data-testid="tab-voicemail">voicemail.conf</TabsTrigger>
            <TabsTrigger value="deploy" data-testid="tab-deploy"><Terminal size={14} className="mr-1.5" />Deploy & Reload</TabsTrigger>
          </TabsList>
          <TabsContent value="pjsip" className="mt-4"><CodeBlock text={cfg.pjsip_conf} filename="pjsip.conf" /></TabsContent>
          <TabsContent value="dialplan" className="mt-4"><CodeBlock text={cfg.extensions_conf} filename="extensions.conf" /></TabsContent>
          <TabsContent value="queues" className="mt-4"><CodeBlock text={cfg.queues_conf} filename="queues.conf" /></TabsContent>
          <TabsContent value="voicemail" className="mt-4"><CodeBlock text={cfg.voicemail_conf} filename="voicemail.conf" /></TabsContent>
          <TabsContent value="deploy" className="mt-4"><CodeBlock text={DEPLOY} filename="install-configs.sh" /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
